import { useEffect, useState, useCallback } from "react";
import { authAxios } from "../../utils/auth";
import { Users, ChevronLeft, ChevronRight, Info } from "lucide-react";

const StatusBadge = ({ status }) => (
  <span
    className={`text-[10px] px-2 py-0.5 rounded-full ${
      status === "Active"
        ? "bg-emerald-500/15 text-emerald-300"
        : "bg-white/5 text-white/40"
    }`}
    title={
      status === "Active"
        ? "Paid tier OR completed a purchase in the last 30 days"
        : "Free tier and no purchase in the last 30 days"
    }
  >
    {status}
  </span>
);

const CustomerPill = ({ isActive }) => (
  <span
    className={`text-[10px] px-2 py-0.5 rounded-full ${
      isActive
        ? "bg-[#C5A55A]/15 text-[#C5A55A]"
        : "bg-white/5 text-white/40"
    }`}
    title={isActive ? "Paid plan or bought in the last 30 days" : "Signed up but not counted as active yet"}
  >
    {isActive ? "Active" : "Signed up"}
  </span>
);

export default function ReferralsList() {
  const [tab, setTab] = useState("all"); // all | customers | affiliates | sub_affiliates
  const [rows, setRows] = useState([]);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ customer_count: 0, affiliate_count: 0, sub_affiliate_count: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(
        `/affiliate/direct?page=${page}&size=20&filter=${tab}`
      );
      setRows(res.data?.affiliates || []);
      setPages(res.data?.pages || 1);
      setTotal(res.data?.total || 0);
      setCounts({
        customer_count:      res.data?.customer_count      || 0,
        affiliate_count:     res.data?.affiliate_count     || 0,
        sub_affiliate_count: res.data?.sub_affiliate_count || 0,
      });
    } catch (e) {
      // silent - handled by outer toast if needed
    } finally {
      setLoading(false);
    }
  }, [page, tab]);

  useEffect(() => { load(); }, [load]);

  const switchTab = (t) => {
    setTab(t);
    setPage(1);
  };

  // "All" count = direct downline (tier 1) total = customers + direct-affiliates.
  // Sub-affiliates is a separate tier-2 count and is NOT summed into All.
  const allDirect = counts.customer_count + counts.affiliate_count;

  return (
    <section className="space-y-3" data-testid="referrals-list">
      {/* Explainer */}
      <div
        className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3.5 text-xs text-white/60 flex items-start gap-2"
        data-testid="referrals-explainer"
      >
        <Info className="w-3.5 h-3.5 text-[#C5A55A] mt-0.5 shrink-0" />
        <div className="leading-relaxed">
          <span className="text-white/80 font-medium">Customers</span> are people who
          signed up through your link — you earn <span className="text-emerald-300">20%</span> on
          everything they buy.{" "}
          <span className="text-white/80 font-medium">Sub-Affiliates</span> are your
          Customers who themselves brought in Customers.{" "}
          <span className="text-white/80 font-medium">Sub-affiliates</span> are the
          Customers your Sub-Affiliates brought in — that&apos;s where your{" "}
          <span className="text-sky-300">6% Sponsor</span> and{" "}
          <span className="text-indigo-300">4% Leadership</span> overrides come from.
        </div>
      </div>

      {/* Sub-tab toggle */}
      <div className="flex items-center gap-1 rounded-lg bg-white/[0.03] p-1 w-fit border border-white/[0.06] flex-wrap">
        {[
          { key: "all",             label: "All",              count: allDirect },
          { key: "customers",       label: "Customers",        count: counts.customer_count },
          { key: "affiliates",      label: "Sub-Affiliates", count: counts.affiliate_count },
          { key: "sub_affiliates",  label: "Sub-affiliates",   count: counts.sub_affiliate_count },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            data-testid={`referrals-subtab-${t.key}`}
            className={`px-3 py-1 text-xs rounded-md transition ${
              tab === t.key
                ? "bg-[#C5A55A]/20 text-[#C5A55A] font-medium"
                : "text-white/50 hover:text-white/70"
            }`}
          >
            {t.label}
            <span className="ml-1.5 text-[10px] opacity-60 font-mono">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/[0.06] bg-[#111113] overflow-hidden">
        {rows.length === 0 && !loading ? (
          <div className="text-center py-10 text-white/40 text-sm" data-testid="referrals-empty">
            <Users className="w-8 h-8 mx-auto mb-2 text-white/15" />
            {tab === "customers"
              ? "No pure customers yet — share your link to start."
              : tab === "affiliates"
              ? "None of your customers have referred anyone yet."
              : tab === "sub_affiliates"
              ? "No sub-affiliates yet — your Sub-Affiliates haven't brought anyone in."
              : "No referrals yet. Copy your affiliate link below to get started."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.02] text-white/40 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">User</th>
                  <th className="text-left py-2 px-3 font-medium">Joined</th>
                  <th className="text-center py-2 px-3 font-medium">Customer</th>
                  <th className="text-center py-2 px-3 font-medium">Sub-Affiliates</th>
                  <th className="text-center py-2 px-3 font-medium">Account</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((aff) => (
                  <tr
                    key={aff.user_id}
                    className="border-t border-white/[0.04] hover:bg-white/[0.02]"
                    data-testid={`referral-row-${aff.user_id}`}
                  >
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#C5A55A]/20 text-[#C5A55A] flex items-center justify-center text-[11px] font-semibold">
                          {aff.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <div className="text-white/80">{aff.name}</div>
                          {aff.subscription_tier && aff.subscription_tier !== "FREE" && (
                            <div className="text-[10px] text-[#C5A55A]/70 uppercase tracking-wider">
                              {aff.subscription_tier}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-white/50 whitespace-nowrap">
                      {aff.joined?.slice(0, 10) || "—"}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <CustomerPill isActive={aff.is_active_customer} />
                    </td>
                    <td className="py-2 px-3 text-center text-white/70 font-mono">
                      {aff.sub_affiliates}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <StatusBadge status={aff.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-white/[0.04] text-[11px] text-white/40">
            <span>
              Page {page} of {pages}
            </span>
            <div className="flex gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
                data-testid="referrals-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                disabled={page >= pages}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
                data-testid="referrals-next"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
